import { useCallback, useEffect, useMemo } from "react"

import { format, startOfDay } from "date-fns"
import { Formik, useFormikContext } from "formik"
import { isEqual } from "lodash"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { useNetworksStateContext } from "@l2r-front/l2r-networks"
import { I18N_NAMESPACE, useCampaignProject } from "@l2r-front/l2r-shared-campaign"
import { L2RButton, ScrollableLayout, useIsMobileDevice } from "@l2r-front/l2r-ui"

import { CampaignColors } from "../../constants/campaignColors"
import { CampaignModules } from "../../constants/modules"

import { validationSchema } from "./CampaignForm.schema"
import * as Styled from "./CampaignForm.styled"

const DATE_FORMAT = "yyyy-MM-dd"

interface FormChangeObserverProps {
    initialValues: any
    onChange?: (_hasChanged: boolean) => void
}

const FormChangeObserver = ({ initialValues, onChange }: FormChangeObserverProps) => {
    const { values } = useFormikContext()

    const compareValues = useCallback(
        (
            values,
            initialValues
        ): boolean => {
            const { tags: initialTags, ...initialFields } = initialValues
            const { tags, ...fields } = values

            const areInitialTags = Boolean(
                (tags?.length === initialTags?.length) &&
                tags?.every((tag: string) => initialTags?.includes(tag))
            )
            const areInitialFields: boolean = isEqual(fields, initialFields)

            return areInitialFields && areInitialTags
        },
        []
    )

    useEffect(() => {
        onChange?.(!compareValues(values, initialValues))
    }, [values, initialValues, compareValues, onChange])

    return null
}

export interface CampaignFormDefaultValues {
    campaign_type: string
    color: string
    comment?: string
    company_name?: string
    date_end: Date | null
    date_start: Date
    name: string
    tags: string[]
}

export interface CampaignFormProps {
    campaigns?: { color: string, name: string }[]
    className?: string
    defaultValues?: CampaignFormDefaultValues
    isSubmitting: boolean
    onCancel: () => void
    onChange?: (_hasChanged: boolean) => void
    onSubmit: (values: any) => void
}

export const CampaignForm = (props: CampaignFormProps) => {
    const {
        campaigns = [],
        className,
        defaultValues,
        isSubmitting,
        onCancel,
        onChange,
        onSubmit,
    } = props

    const isEditMode = !!defaultValues

    const { t } = useTranslation(I18N_NAMESPACE)
    const isMobile = useIsMobileDevice()
    const { data: project } = useCampaignProject()
    const { selectedNetwork, selectedSubNetworkSlugs } = useNetworksStateContext()

    const existingCampaignNames = useMemo(() => {
        return campaigns.map(campaign => campaign.name)
    }, [campaigns])

    const leastUsedColor = useMemo(() => {
        const usageCounts = CampaignColors.map(color => {
            return campaigns.filter(campaign => campaign.color === color.value).length
        })
        const leastUsedIndex = usageCounts.indexOf(Math.min(...usageCounts))
        return CampaignColors[leastUsedIndex].value
    }, [campaigns])

    const initialModules = new Set(selectedNetwork?.modules || new Set())
    const enabledModulesForNetwork = Array.from(selectedNetwork?.children?.length ? selectedSubNetworkSlugs.map(slug => {
        return selectedNetwork.children.find(child => child.slug === slug)
    }).reduce((acc, subNetwork) => {
        subNetwork.modules.forEach(module => acc.add(module))
        return acc
    }, new Set()) : initialModules)
        .filter((module: string) => {
            return CampaignModules
                .some(campaignModule => campaignModule.module === module)
        })

    const displayedModules = enabledModulesForNetwork.map(module => {
        return CampaignModules.find(campaignModule => campaignModule.module === module)
    })
        .sort((a, b) => a.order - b.order)

    const initialValues = useMemo(() => {
        if (defaultValues) {
            return defaultValues
        }
        return {
            color: leastUsedColor,
            date_start: startOfDay(new Date()),
            date_end: null,
            tags: [],
        }
    }, [defaultValues, leastUsedColor])

    const handleSubmit = useCallback((values) => {
        onSubmit({
            ...values,
            date_start: values.date_start ? format(values.date_start, DATE_FORMAT) : null,
            date_end: values.date_end ? format(values.date_end, DATE_FORMAT) : null,
        })
    }, [onSubmit])

    return <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema(t, existingCampaignNames)}
    >
        {({ errors, values }) => {
            const hasErrors = Object.values(errors).some(error => !!error)
            const areInitialValues = isEqual(values, initialValues)
            return <Styled.Form>
                <FormChangeObserver initialValues={initialValues} onChange={onChange} />
                <ScrollableLayout
                    className={className}
                    content={
                        <Styled.FormContent isMobile={isMobile}>
                            <Styled.FieldsWrapper isMobile={isMobile}>
                                {isEditMode ? (
                                    <Styled.TextField
                                        id="campaign_type"
                                        name="campaign_type"
                                        type="text"
                                        disabled
                                        label={`${t(I18N_NAMESPACE, "forms.campaignForm.inputs.campaign_type")}*`}
                                        value={t(I18N_NAMESPACE, CampaignModules.find(module => module.module === values.campaign_type)?.labelKey)}
                                    />
                                ) : (
                                    <Styled.Select
                                        id="campaign_type"
                                        name="campaign_type"
                                        value={values.campaign_type || ""}
                                        label={`${t(I18N_NAMESPACE, "forms.campaignForm.inputs.campaign_type")}*`}>
                                        {displayedModules.map(module => {
                                            const Icon = module.icon
                                            return (
                                                <Styled.MenuItem key={module.module} value={module.module}>
                                                    <Icon color="action/active" />
                                                    {t(I18N_NAMESPACE, module.labelKey)}
                                                </Styled.MenuItem>
                                            )
                                        })}
                                    </Styled.Select>
                                )}
                                <Styled.TextField
                                    id="campaign_name"
                                    name="name"
                                    type="text"
                                    label={`${t(I18N_NAMESPACE, "forms.campaignForm.inputs.name")}*`}
                                    value={values.name}
                                    slotProps={{ htmlInput: { maxLength: 50 } }}
                                />
                                <Styled.DatePicker
                                    id="date_start"
                                    name="date_start"
                                    format="dd/MM/yyyy"
                                    label={`${t(I18N_NAMESPACE, "forms.campaignForm.inputs.date_start")}*`}
                                    value={values.date_start}
                                />
                                <Styled.DatePicker
                                    id="date_end"
                                    name="date_end"
                                    format="dd/MM/yyyy"
                                    label={`${t(I18N_NAMESPACE, "forms.campaignForm.inputs.date_end")}`}
                                    value={values.date_end}
                                    minDate={values.date_start}
                                />
                                <Styled.Select
                                    id="color"
                                    name="color"
                                    value={values.color || ""}
                                    label={`${t(I18N_NAMESPACE, "forms.campaignForm.inputs.color")}*`}>
                                    {CampaignColors.map(color => (
                                        <Styled.MenuItem key={color.key} value={color.value}>
                                            <Styled.Color color={color.value} />
                                            {t(I18N_NAMESPACE, color.label)}
                                        </Styled.MenuItem>
                                    ))}
                                </Styled.Select>
                                <Styled.TagsAutocomplete
                                    name="tags"
                                    variant="outlined"
                                    tags={values.tags}
                                    existingTags={(project?.tags || [])}
                                />
                                <Styled.TextField
                                    id="company_name"
                                    name="company_name"
                                    type="text"
                                    label={`${t(I18N_NAMESPACE, "forms.campaignForm.inputs.company_name")}`}
                                    value={values.company_name}
                                    slotProps={{ htmlInput: { maxLength: 50 } }}
                                />
                                <Styled.TextField
                                    id="comment"
                                    name="comment"
                                    type="text"
                                    label={`${t(I18N_NAMESPACE, "forms.campaignForm.inputs.description")}`}
                                    value={values.comment}
                                    slotProps={{ htmlInput: { maxLength: 500 } }}
                                    multiline
                                />
                            </Styled.FieldsWrapper>
                        </Styled.FormContent>}
                    buttons={
                        <>
                            <L2RButton
                                id="cancel-button"
                                onClick={() => {
                                    onCancel()
                                }}
                                variant="tertiary"
                            >
                                {t(I18N_NAMESPACE, "forms.campaignForm.buttons.cancel")}
                            </L2RButton>
                            <Styled.SubmitButton
                                id="submit-button"
                                type="submit"
                                disabled={hasErrors || isSubmitting || areInitialValues}
                            >
                                {t(I18N_NAMESPACE, "forms.campaignForm.buttons.save")}
                            </Styled.SubmitButton>
                        </>
                    }
                />
            </Styled.Form>
        }}
    </Formik >
}
